import Icon, { CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Input, message, Popover, Tag, Tooltip } from 'antd';
import Empty from 'components/Empty';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import useSmartField from 'hooks/useSmartField';
import { HTMLAttributes, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { getAdminStoresByIds } from 'services/api/admin';
import { aggregationWabaList } from 'services/api/whatsapp';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { ReactComponent as iconMore } from 'views/Setting/BlockList/assets/more.svg';
import { PlatformIcon } from 'components/PlatformIcon';
import { CountryFlagIcon } from 'components/CountryFlagIcon';
import { FV, Store } from './share';
import useCheck from './useCheck';
import { useGetRegionLabel } from 'hooks/business/useRegion';

export const getStoreCount = (storeScopes: General.StoreScope[]) => {
  return storeScopes.reduce((res, platformScope) => {
    return platformScope.regionStoreScopes.reduce((res, regionScope) => {
      const { storeCount, storeIds } = regionScope;

      return res + ((storeIds || []).length || storeCount || 0);
    }, res);
  }, 0);
};

export const getScopesStoreMap = (
  storeScopes: General.StoreScope[],
  currentStoreMap?: Partial<Record<string, Store>>,
) => {
  const checkedStoreIds = storeScopes.reduce<number[]>((res, platformScope) => {
    const { platform, regionStoreScopes } = platformScope;

    if (platform === 'WA_BIZ') {
      return res;
    }

    return regionStoreScopes.reduce((res, regionScope) => {
      res.push(...(regionScope.storeIds || []));

      return res;
    }, res);
  }, []);

  const storeIds = currentStoreMap
    ? checkedStoreIds.filter((storeId) => !currentStoreMap[storeId])
    : checkedStoreIds;

  if (storeIds.length === 0) {
    return Promise.resolve(currentStoreMap || {});
  }

  return getAdminStoresByIds(storeIds).then(({ stores }) => {
    if (stores.length === 0) {
      return currentStoreMap || {};
    }

    const nextStoreMap = stores.reduce(
      (res, store) => {
        res[store.id] = store;

        return res;
      },
      { ...currentStoreMap },
    );

    return nextStoreMap;
  });
};

export interface ResultProps extends HTMLAttributes<HTMLDivElement> {
  editable?: boolean;
  showTitle?: boolean;
  showSearch?: boolean;
  ellipsis?: number;
}

function Result(props: ResultProps) {
  const {
    editable = true,
    showTitle = true,
    showSearch = true,
    ellipsis = Infinity,
    ...otherProps
  } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const { config: storeNameConfig } = useStoreNameConfig();

  const {
    field: { value, onChange },
  } = useSmartField<FV['storeScopes']>('storeScopes');

  const {
    field: { value: storeMap, onChange: onStoreMapChange },
  } = useSmartField<FV['assets']['storeMap']>('assets.storeMap');

  const {
    field: { value: wabaMap, onChange: onWabaMapChange },
  } = useSmartField<FV['assets']['wabaMap']>('assets.wabaMap');

  const refsCurrent = {
    onStoreMapChange,
    onWabaMapChange,
    t,
    storeMap,
    wabaMap,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    getScopesStoreMap(value, refs.current.storeMap)
      .then((nextStoreMap) => {
        refs.current.onStoreMapChange(nextStoreMap);
      })
      .catch(() => {
        message.error(refs.current.t('marketing.获取店铺列表失败，请稍后再试'));
      });
  }, [value]);

  useEffect(() => {
    const checkedWabaIds = value.reduce<number[]>((res, platformScope) => {
      const { platform, regionStoreScopes } = platformScope;

      if (platform !== 'WA_BIZ') {
        return res;
      }

      return regionStoreScopes.reduce((res, regionScope) => {
        res.push(...(regionScope.storeIds || []));

        return res;
      }, res);
    }, []);

    const wabaIds = checkedWabaIds.filter((wabaId) => !refs.current.wabaMap[wabaId]);

    if (wabaIds.length === 0) {
      return;
    }

    aggregationWabaList({
      tunnelAccountIds: wabaIds,
    })
      .then(({ accounts }) => {
        if (accounts.length === 0) {
          return;
        }

        const nextWabaMap = accounts.reduce(
          (res, waba) => {
            res[waba.tunnelAccountId] = waba;

            return res;
          },
          { ...refs.current.wabaMap },
        );

        refs.current.onWabaMapChange(nextWabaMap);
      })
      .catch(() => {
        message.error(refs.current.t('department.获取waba列表失败，请稍后再试'));
      });
  }, [value]);

  const [keywords, setKeywords] = useState('');

  const { onChecked, onPlatformCheck, onRegionCheck } = useCheck();

  const resultList = useMemo(() => {
    const originList = value
      .reduce<{ priority: number; storeCount: number; element: ReactNode }[]>(
        (res, platformScope) => {
          const { platform, regionStoreScopes } = platformScope;

          return regionStoreScopes.reduce((res, regionScope) => {
            const { region, storeCount, storeIds } = regionScope;

            if (!storeIds || storeIds.length === 0) {
              if (keywords) {
                return res;
              }

              if (region === 'all') {
                res.push({
                  priority: 0,
                  storeCount: getStoreCount([platformScope]),
                  element: (
                    <Item key={platform} editable={editable}>
                      <div>
                        <Tooltip title={platformLabelMap[toLowerCase(platform)]}>
                          <PlatformIcon platform={platform} />
                        </Tooltip>
                        {t('feedback.全部')}
                        {`(${storeCount})`}
                        {platform === 'TIKTOK_US' && (
                          <Tag
                            className="text-success border border-solid border-success !bg-fill-0"
                            color="green"
                          >
                            {t('dept_tag_us')}
                          </Tag>
                        )}
                      </div>
                      {editable && (
                        <Button
                          type="text"
                          icon={<CloseOutlined />}
                          onClick={() =>
                            onPlatformCheck(false, {
                              platform,
                              count: storeCount,
                            })
                          }
                        />
                      )}
                    </Item>
                  ),
                });

                return res;
              }

              const regionLabel = getRegionLabel(region);

              res.push({
                priority: 1,
                storeCount: (storeIds || []).length || storeCount || 0,
                element: (
                  <Item key={`${platform}@${region}`} editable={editable}>
                    <div>
                      <Tooltip title={`${platformLabelMap[toLowerCase(platform)]} ${regionLabel}`}>
                        <PlatformIcon platform={platform} />
                        <CountryFlagIcon region={region} />
                      </Tooltip>
                      {t('feedback.全部')}
                      {`(${storeCount})`}
                    </div>
                    {editable && (
                      <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={() =>
                          onRegionCheck(false, {
                            platform,
                            region,
                            count: storeCount,
                          })
                        }
                      />
                    )}
                  </Item>
                ),
              });

              return res;
            }

            return storeIds.reduce((res, storeId) => {
              const close = (
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() =>
                    onChecked(false, [
                      {
                        platform,
                        region,
                        id: storeId,
                      },
                    ])
                  }
                />
              );

              if (platform === 'WA_BIZ') {
                const { [storeId]: waba } = wabaMap;

                if (!waba) {
                  return res;
                }

                const { phone } = waba;

                if (!phone.includes(keywords)) {
                  return res;
                }

                res.push({
                  priority: 2,
                  storeCount: 1,
                  element: (
                    <Item key={storeId} editable={editable}>
                      <div>
                        <PlatformIcon platform="WA_BIZ" />
                        {phone}
                      </div>
                      {editable && close}
                    </Item>
                  ),
                });

                return res;
              }

              if (region === 'all') {
                return res;
              }

              const { [storeId]: store } = storeMap;

              if (!store) {
                return res;
              }

              const { externalId, storeName, alias } = store;

              if (
                !externalId.includes(keywords) &&
                !storeName.includes(keywords) &&
                !alias.includes(keywords)
              ) {
                return res;
              }

              const regionLabel = getRegionLabel(region);

              res.push({
                priority: 2,
                storeCount: 1,
                element: (
                  <Item key={storeId} editable={editable}>
                    <div>
                      <Tooltip
                        title={`${platformLabelMap[toLowerCase(platform)]}${
                          platform === 'FACEBOOK' ? '' : ` ${regionLabel}`
                        }`}
                      >
                        <PlatformIcon platform={platform} />
                        {platform !== 'FACEBOOK' && <CountryFlagIcon region={region} />}
                      </Tooltip>
                      {isStoreAliasFirst(storeNameConfig, platform, region)
                        ? alias || storeName
                        : storeName}
                    </div>
                    {editable && close}
                  </Item>
                ),
              });

              return res;
            }, res);
          }, res);
        },
        [],
      )
      .sort((a, b) => a.priority - b.priority);

    if (originList.length <= ellipsis) {
      return originList.map(({ element }) => element);
    }

    const moreList = originList.slice(ellipsis - 1);

    const moreCount = moreList.reduce((res, { storeCount }) => res + storeCount, 0);

    return [
      ...originList.slice(0, ellipsis - 1).map(({ element }) => element),
      <Popover
        key="ellipsis"
        content={
          <MoreStorePopContent>{moreList.map(({ element }) => element)}</MoreStorePopContent>
        }
      >
        <More>
          {t('group.更多x家店铺', { total: moreCount })} <Icon component={iconMore} />
        </More>
      </Popover>,
    ];
  }, [
    keywords,
    editable,
    ellipsis,
    onChecked,
    onPlatformCheck,
    onRegionCheck,
    storeMap,
    value,
    wabaMap,
    storeNameConfig,
    t,
    getRegionLabel,
  ]);

  const resultCount = useMemo(() => getStoreCount(value), [value]);

  return (
    <Container {...otherProps}>
      {showTitle && (
        <header>
          <span>{t('department.已选店铺(x)', { x: resultCount })}</span>
          {editable && resultCount > 0 && (
            <Tooltip title={t('department.清空')}>
              <Button type="text" icon={<DeleteOutlined />} onClick={() => onChange([])} />
            </Tooltip>
          )}
        </header>
      )}
      {showSearch && (
        <header>
          <Input.Search placeholder={t('store_list_search_placeholder')} onSearch={setKeywords} />
        </header>
      )}
      <List>{resultList.length > 0 ? resultList : <Empty />}</List>
    </Container>
  );
}

export default Result;

const Container = styled.div`
  background: var(--fett-erp-card-bg);
  overflow: auto;
  > header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 44px;
    padding: 0 8px;
    border-bottom: solid 1px var(--fett-border-default);
  }
  > div {
    flex: 1 0 0;
  }
`;

const Item = styled.div<{ editable: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 32px;
  padding: 0 6px;
  cursor: ${({ editable }) => (editable ? 'pointer' : 'auto')};
  &:hover {
    background: ${({ editable }) => (editable ? 'var(--fett-bg-panel)' : 'transparent')};
  }
  > div {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    > .anticon,
    > img,
    > span > img {
      margin-right: 4px;
    }
  }
`;

const List = styled.div`
  padding: 8px;
`;

const MoreStorePopContent = styled.div`
  max-height: 280px;
  margin: -12px -16px;
  padding: 12px 16px;
  overflow: auto;
`;

const More = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  width: fit-content;
  padding: 0 6px;
  cursor: pointer;
  .anticon {
    margin: 0;
  }
`;
